import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconRoute, IconTrash, IconTruckDelivery } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { CarrierSelect } from '../shipMethods/CarrierSelect';
import { PickStatusBadge } from './PickStatusBadge';
import type { PickStatus } from './api';
import {
  getWave,
  listWaveLines,
  listWaveOrders,
  removeWave,
  shipWaveOrder,
  type PickWaveLineDto,
  type PickWaveOrderDto,
} from './waveApi';

/**
 * One wave.
 *
 * **Two questions, and they are different ones.** The lines say what to fetch — the summed total
 * of each item, in stocking units, which is the whole point of a wave. Opening a line says which
 * orders are waiting on that item and how much each takes, which is what turns the tote back into
 * shipments at the bench. legacy has a screen for each; here the second is a click on the first.
 *
 * **Walking it is the ordinary pick screen.** A wave is a pick, so there is no wave-shaped picking
 * UI — the button goes to the same directed pick route everything else uses.
 */
export function PickWaveDetailRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const { id } = useParams({ from: '/app/pick-waves/$id' });
  const [openItem, setOpenItem] = useState<PickWaveLineDto | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const canBuild = permissions.has('orders:write');

  const wave = useQuery({
    queryKey: ['pick-wave', id] as const,
    queryFn: ({ signal }) => getWave(id, signal),
  });

  const lines = useQuery({
    queryKey: ['pick-wave-lines', id] as const,
    queryFn: ({ signal }) => listWaveLines(id, signal),
  });

  const remove = useMutation({
    mutationFn: () => removeWave(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pick-waves'] });
      await queryClient.invalidateQueries({ queryKey: ['pick-wave-candidates'] });
      navigate({ to: '/app/pick-waves' });
    },
  });

  const columns = useMemo<DataColumn<PickWaveLineDto>[]>(() => [
    {
      key: 'itemNumber',
      label: t('waves.item'),
      render: (l) => (
        <Stack gap={0}>
          <Text fw={600}>{l.itemNumber}</Text>
          {l.itemDescription ? <Text size="xs" c="dimmed">{l.itemDescription}</Text> : null}
        </Stack>
      ),
      card: 'title',
    },
    {
      key: 'quantityRequested',
      label: t('waves.toFetch'),
      align: 'right',
      render: (l) => `${l.quantityRequested} ${l.uomCode}`,
    },
    {
      key: 'quantityPicked',
      label: t('waves.picked'),
      align: 'right',
      render: (l) => l.quantityPicked,
    },
    {
      key: 'orderCount',
      label: t('waves.forOrders'),
      align: 'right',
      render: (l) => <Badge variant="light">{l.orderCount}</Badge>,
      card: 'aside',
    },
  ], [t]);

  if (wave.isLoading) {
    return <Center py="xl"><Loader /></Center>;
  }

  if (wave.isError || !wave.data) {
    return (
      <Stack>
        <PageHeader title={t('waves.title')} back />
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {wave.error instanceof ApiError ? wave.error.problem.title : t('waves.notFound')}
        </Alert>
      </Stack>
    );
  }

  const w = wave.data;
  // Once anything has been fetched the wave cannot be broken up — the stock is on the dock and
  // deleting the document would leave it there with nothing saying why. The server refuses it
  // too (54178); hiding the button is so nobody reaches for it.
  const canRemove = canBuild && w.unitsPicked === 0;

  return (
    <Stack>
      <PageHeader
        title={w.pickNumber}
        subtitle={t('waves.detailSubtitleShipped', {
          orders: w.orderCount, items: w.itemCount, shipped: w.shippedOrderCount,
        })}
        back
        actions={(
          <Group gap="xs">
            {canRemove ? (
              <Button
                variant="default"
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={() => setConfirmRemove(true)}
              >
                {t('waves.remove')}
              </Button>
            ) : null}
            <Button
              leftSection={<IconRoute size={16} />}
              onClick={() => navigate({ to: '/app/picks/$id', params: { id: w.pickId } })}
            >
              {t('waves.walk')}
            </Button>
          </Group>
        )}
      />

      <Card withBorder padding="sm">
        <Group>
          <PickStatusBadge status={w.status as PickStatus} />
          <Text size="sm" c="dimmed">
            {t('waves.progress', { picked: w.unitsPicked, requested: w.unitsRequested })}
          </Text>
        </Group>
      </Card>

      {lines.isLoading ? (
        <Center py="xl"><Loader /></Center>
      ) : lines.data && lines.data.length > 0 ? (
        <ResponsiveTable
          rows={lines.data}
          columns={columns}
          rowKey={(l) => l.pickLineId}
          minWidth={640}
          actions={(l) => (
            <Button size="compact-sm" variant="subtle" onClick={() => setOpenItem(l)}>
              {t('waves.whoWantsIt')}
            </Button>
          )}
        />
      ) : (
        <Text c="dimmed">{t('waves.noLines')}</Text>
      )}

      <WaveOrders waveId={id} canShip={canBuild} />

      <OrdersForItem waveId={id} line={openItem} onClose={() => setOpenItem(null)} />

      <Modal
        opened={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        title={t('waves.removeTitle')}
      >
        <Stack>
          <Text>{t('waves.removeBody', { number: w.pickNumber, count: w.orderCount })}</Text>
          {remove.isError ? (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {remove.error instanceof ApiError ? remove.error.problem.title : t('waves.removeFailed')}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmRemove(false)}>
              {t('shell.cancel')}
            </Button>
            <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
              {t('waves.remove')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

interface OrdersProps {
  waveId: string;
  line: PickWaveLineDto | null;
  onClose: () => void;
}

/** Which orders are waiting on one item, and how much each one takes. */
function OrdersForItem({ waveId, line, onClose }: OrdersProps) {
  const { t } = useTranslation();

  const orders = useQuery({
    queryKey: ['pick-wave-orders', waveId, line?.itemId] as const,
    queryFn: ({ signal }) => listWaveOrders(waveId, line!.itemId, signal),
    enabled: Boolean(line),
  });

  const columns = useMemo<DataColumn<PickWaveOrderDto>[]>(() => [
    {
      key: 'orderNumber',
      label: t('waves.order'),
      render: (o) => <Text fw={600}>{o.orderNumber}</Text>,
      card: 'title',
    },
    {
      key: 'customer',
      label: t('waves.customer'),
      render: (o) => o.companyName ?? o.customerNumber,
    },
    {
      key: 'quantity',
      label: t('waves.takes'),
      align: 'right',
      render: (o) => o.quantity,
    },
  ], [t]);

  return (
    <Modal
      opened={Boolean(line)}
      onClose={onClose}
      title={line ? t('waves.whoWantsTitle', { item: line.itemNumber }) : ''}
      size="lg"
    >
      {orders.isLoading ? (
        <Center py="xl"><Loader /></Center>
      ) : orders.data && orders.data.length > 0 ? (
        <ResponsiveTable
          rows={orders.data}
          columns={columns}
          rowKey={(o) => o.salesOrderId}
          minWidth={520}
        />
      ) : (
        <Text c="dimmed">{t('waves.noOrdersForItem')}</Text>
      )}
    </Modal>
  );
}

/**
 * The wave's member orders, and shipping them.
 *
 * **One at a time, out of the same dock.** A wave is picked once and shipped many times: each
 * order takes what it is still owed, and one that only gets part of it becomes a back order. That
 * is the whole of M5.7's second half, and the reason there is no single Ship button on the wave —
 * pressing one would send the whole dock with no order marked as gone, which is precisely what
 * legacy's own wave ship does.
 */
function WaveOrders({ waveId, canShip }: { waveId: string; canShip: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [shipping, setShipping] = useState<PickWaveOrderDto | null>(null);
  const [tracking, setTracking] = useState('');
  const [bol, setBol] = useState('');
  const [shipMethodId, setShipMethodId] = useState<string | null>(null);

  const orders = useQuery({
    queryKey: ['pick-wave-orders', waveId, undefined] as const,
    queryFn: ({ signal }) => listWaveOrders(waveId, undefined, signal),
  });

  const ship = useMutation({
    mutationFn: () => shipWaveOrder(waveId, shipping!.salesOrderId, {
      shipMethodId,
      trackingNumber: tracking.trim() || null,
      billOfLading: bol.trim() || null,
    }),
    onSuccess: async () => {
      setShipping(null);
      setTracking('');
      setBol('');
      setShipMethodId(null);
      await queryClient.invalidateQueries({ queryKey: ['pick-wave-orders', waveId] });
      await queryClient.invalidateQueries({ queryKey: ['pick-wave', waveId] });
      await queryClient.invalidateQueries({ queryKey: ['pick-waves'] });
    },
  });

  const columns = useMemo<DataColumn<PickWaveOrderDto>[]>(() => [
    {
      key: 'orderNumber',
      label: t('waves.order'),
      render: (o) => <Text fw={600}>{o.orderNumber}</Text>,
      card: 'title',
    },
    {
      key: 'customer',
      label: t('waves.customer'),
      render: (o) => o.companyName ?? o.customerNumber,
    },
    {
      key: 'state',
      label: t('waves.shipped'),
      render: (o) => (o.shippedAtUtc
        ? <Badge color="green" variant="light">{t('waves.gone')}</Badge>
        : <Badge color="gray" variant="light">{t('waves.onTheDock')}</Badge>),
      card: 'aside',
    },
    {
      key: 'tracking',
      label: t('waves.tracking'),
      render: (o) => o.trackingNumber ?? '—',
    },
  ], [t]);

  return (
    <>
      <Text fw={600} mt="md">{t('waves.ordersOnThisWave')}</Text>

      {orders.isLoading ? (
        <Center py="md"><Loader /></Center>
      ) : orders.data && orders.data.length > 0 ? (
        <ResponsiveTable
          rows={orders.data}
          columns={columns}
          rowKey={(o) => o.salesOrderId}
          minWidth={640}
          actions={(o) => (canShip && !o.shippedAtUtc ? (
            <Button
              size="compact-sm"
              variant="subtle"
              leftSection={<IconTruckDelivery size={14} />}
              onClick={() => setShipping(o)}
            >
              {t('waves.ship')}
            </Button>
          ) : null)}
        />
      ) : (
        <Text c="dimmed">{t('waves.noOrders')}</Text>
      )}

      <Modal
        opened={Boolean(shipping)}
        onClose={() => setShipping(null)}
        title={shipping ? t('waves.shipTitle', { number: shipping.orderNumber }) : ''}
      >
        <Stack>
          {/* Said before it happens, because it is the part people get wrong: a short
              shipment does not finish the order, it turns it into a back order. */}
          <Text size="sm" c="dimmed">{t('waves.shipBody')}</Text>

          {/* Per order, not per wave: the orders on one wave go to different customers, very
              likely by different carriers. */}
          <CarrierSelect value={shipMethodId} onChange={setShipMethodId} />

          <TextInput
            label={t('waves.tracking')}
            value={tracking}
            onChange={(e) => setTracking(e.currentTarget.value)}
          />
          <TextInput
            label={t('waves.bol')}
            value={bol}
            onChange={(e) => setBol(e.currentTarget.value)}
          />

          {ship.isError ? (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {ship.error instanceof ApiError ? ship.error.problem.title : t('waves.shipFailed')}
            </Alert>
          ) : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShipping(null)}>{t('shell.cancel')}</Button>
            <Button loading={ship.isPending} onClick={() => ship.mutate()}>
              {t('waves.ship')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
